/**
 * 필터 고르는 팝오버.
 *
 * **네이티브 `<select>` 넷을 이것 하나로 바꿨다**(2026-08-26). 이유가 셋이다.
 *
 * ① `<select>` 는 **글자만** 보여준다. 카드에는 타입이 도형(`●`), 우선순위가
 *    화살표(`↑`), 담당자가 아바타로 그려지는데 필터에서는 「버그」·「높음」
 *    같은 글자였다 — 같은 것을 두 가지 언어로 말하고 있었다. 여기서는
 *    **카드와 똑같은 조각**(`TypeMark`·`PriorityMark`·`AssigneeMark`·
 *    `LabelChip`)을 그대로 쓴다.
 * ② 안 걸린 선택기 넷이 **항상 자리를 먹었다.** 좁은 화면에서 도구줄이
 *    세 줄이 됐고, 그중 두 줄이 「아직 아무것도 안 고름」이었다.
 * ③ 축을 여럿 걸 때 선택기를 하나씩 열고 닫아야 했다. 여기서는 한 번 열어
 *    다 만진다.
 *
 * 팝오버는 **하나뿐이다.** 축마다 팝오버를 두면 레이어가 겹치고 좁은
 * 화면에서 화면 밖으로 밀린다 — 그것이 원래 네이티브 선택기를 골랐던
 * 이유였고, 지금은 그 문제가 없다.
 */

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { ROLE_LABEL, type Label, type TicketFilter, type UserBrief } from '../api';
import { DEVICES, ENVIRONMENTS, KINDS, PRIORITIES, TYPES } from '../columns';
import { AssigneeMark, LabelChip, PriorityMark, TypeMark } from './Chips';

/** 고를 수 있는 것 하나. 켜져 있으면 브랜드색 테두리가 붙는다. */
function Choice({
  on,
  onClick,
  children,
  label,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      className="inline-flex min-h-s7 items-center gap-s2 rounded-md border px-s3 text-meta transition-colors max-[720px]:min-h-[44px]"
      style={{
        borderColor: on ? 'var(--brand)' : 'var(--line)',
        background: on ? 'var(--brand-t)' : 'transparent',
        color: 'var(--ink)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * 필터 한 묶음. **선으로만 가른다**(2026-08-26 지시).
 *
 * 소제목이 없어도 무엇을 고르는지 읽힌다 — **값 자체가 말하기 때문이다.**
 * 아바타가 있으면 담당자, 도형이면 작업 유형, 화살표면 우선순위, 색 칩이면
 * 라벨이다. 카드에서 쓰는 것과 같은 그림이라 이미 배운 것을 다시 읽는다.
 *
 * `title` 은 읽는 쪽에만 준다.
 */
function Section({
  title,
  caption = false,
  children,
}: {
  title: string;
  /**
   * 이름표를 **보이게** 단다. 담당자·작성자는 같은 사람 목록이라 값만으로는
   * 어느 축인지 안 갈린다(2026-10-19) — 그 둘과 보류에만 쓴다.
   */
  caption?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="border-b border-line px-s4 py-s3 last:border-b-0"
    >
      {caption && <p className="m-0 mb-s2 text-small font-bold text-ink-3">{title}</p>}
      <div className="flex flex-wrap gap-s2">{children}</div>
    </section>
  );
}

/** 사람 하나. **나는 맨 앞에 「나」를 붙여 세운다** — 「내 작업」·「내가 낸 것」 자리다. */
function People({
  members,
  me,
  selected,
  axis,
  onPick,
}: {
  members: UserBrief[];
  me: number;
  selected: number | null | undefined;
  axis: string;
  onPick: (id: number) => void;
}) {
  const ordered = [...members].sort((a, b) => Number(b.id === me) - Number(a.id === me));
  return ordered.map((m) => (
    <Choice
      key={m.id}
      on={selected === m.id}
      onClick={() => onPick(m.id)}
      label={`${axis} ${m.display_name}`}
    >
      <AssigneeMark user={m} size={18} />
      {m.display_name}
      {m.id === me ? (
        <span className="ml-s1 text-micro font-bold text-brand">나</span>
      ) : (
        <span className="ml-s1 text-micro text-ink-3">{ROLE_LABEL[m.role]}</span>
      )}
    </Choice>
  ));
}

export function FilterMenu({
  filter,
  onFilter,
  members,
  labels,
  me,
  children,
}: {
  filter: TicketFilter;
  onFilter: (next: TicketFilter) => void;
  members: UserBrief[];
  labels: Label[];
  /** 지금 쓰는 사람. 사람 목록에서 맨 앞에 「나」로 선다. */
  me: number;
  children: React.ReactNode;
}) {
  /** 같은 값을 다시 누르면 그 축을 푼다 — 라디오가 아니라 토글이다. */
  function toggle<K extends keyof TicketFilter>(key: K, value: TicketFilter[K]) {
    onFilter({ ...filter, [key]: filter[key] === value ? null : value });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="max-h-[var(--radix-popover-content-available-height)] w-[300px] overflow-y-auto p-0">
        {/* **도구줄의 「내 작업」·「내가 낸 것」·「보류」가 여기로 들어왔다**
            (2026-10-19 사용자 지시). 담당자에서 「나」= 내 작업, 작성자에서
            「나」= 내가 낸 것이다. 두 축은 칸이 달라(`assignee_id`·
            `reporter_id`) 함께 걸어도 모순이 없다 — 둘 다 「나」면 내가 내고
            내가 잡은 것. ⚠ 기획·QA 가 만든 티켓은 담당자가 비므로 자기 이슈를
            찾을 때는 담당자가 아니라 작성자다(2026-09-01 오독). */}
        {members.length > 0 && (
          <>
            <Section title="담당자" caption>
              <People members={members} me={me} selected={filter.assignee_id} axis="담당자"
                onPick={(id) => toggle('assignee_id', id)} />
            </Section>
            <Section title="작성자" caption>
              <People members={members} me={me} selected={filter.reporter_id} axis="작성자"
                onPick={(id) => toggle('reporter_id', id)} />
            </Section>
          </>
        )}

        {/* 보류(2026-09-02 사용자 확정). **칸이 아니라 보이나 안 보이나**다 —
            아무것도 안 고르면 서버 기본(전부 준다). 같은 값을 다시 누르면 푼다.
            ⚠ `false` 도 고른 값이라 `===` 로 비교한다. */}
        <Section title="보류" caption>
          <Choice on={filter.held === false} onClick={() => toggle('held', false)} label="보류 숨김">
            보류 숨김
          </Choice>
          <Choice on={filter.held === true} onClick={() => toggle('held', true)} label="보류만">
            보류만
          </Choice>
        </Section>

        {/* **유형이 맨 위다.** 「이슈만 보기」가 이 화면에서 가장 자주 쓰는
            거르기이고, 그것을 고르면 아래 디바이스·환경이 뜻을 갖는다. */}
        <Section title="유형">
          {KINDS.map((k) => (
            <Choice
              key={k.id}
              on={filter.kind === k.id}
              onClick={() => toggle('kind', k.id)}
              label={`유형 ${k.label}`}
            >
              {k.label}
            </Choice>
          ))}
        </Section>

        {/* 이슈에만 있는 축이라, **유형이 이슈일 때만** 보여 준다.
            할 일을 보고 있는데 「디바이스」가 떠 있으면 고를 수 있는 줄 안다. */}
        {filter.kind === 'issue' && (
          <>
            <Section title="디바이스">
              {DEVICES.map((d) => (
                <Choice
                  key={d.id}
                  on={filter.device === d.id}
                  onClick={() => toggle('device', d.id)}
                  label={`디바이스 ${d.label}`}
                >
                  {d.label}
                </Choice>
              ))}
            </Section>
            <Section title="환경">
              {ENVIRONMENTS.map((e) => (
                <Choice
                  key={e.id}
                  on={filter.environment === e.id}
                  onClick={() => toggle('environment', e.id)}
                  label={`환경 ${e.label}`}
                >
                  {e.label}
                </Choice>
              ))}
            </Section>
          </>
        )}

        <Section title="작업 유형">
          {TYPES.map((t) => (
            <Choice
              key={t.id}
              on={filter.type === t.id}
              onClick={() => toggle('type', t.id)}
              label={`타입 ${t.label}`}
            >
              {/* **배지가 곧 이름이다.** 옛 판은 도형 옆에 글자를 따로
                  적었는데, 배지가 되면서 그 글자가 두 번 나온다. */}
              <TypeMark type={t.id} />
            </Choice>
          ))}
        </Section>

        <Section title="우선순위">
          {PRIORITIES.map((p) => (
            <Choice
              key={p.id}
              on={filter.priority === p.id}
              onClick={() => toggle('priority', p.id)}
              label={`우선순위 ${p.label}`}
            >
              <PriorityMark priority={p.id} />
            </Choice>
          ))}
        </Section>

        {labels.length > 0 && (
          <Section title="라벨">
            {labels.map((l) => (
              <Choice
                key={l.id}
                on={filter.label_id === l.id}
                onClick={() => toggle('label_id', l.id)}
                label={`라벨 ${l.name}`}
              >
                {/* 안 고른 라벨은 색을 뺀다 — 카드의 라벨 토글과 같은 규칙이다. */}
                <LabelChip label={l} muted={filter.label_id !== l.id} />
              </Choice>
            ))}
          </Section>
        )}
      </PopoverContent>
    </Popover>
  );
}
